// Parser for CSV files of the analyst (todo/20, U1). No DOM; the bundled datasets keep parseCsv of
// dataset-loader.js.
//
// Format: separator ',' or ';' (the one that occurs more often outside quotes in the header line), quoted fields
// with separators, line breaks, and doubled quotes, line ends \r\n, \n, or \r, optional byte-order mark. Blank
// lines are skipped. Header names and unquoted values are trimmed; quoted values stay as written. An empty field
// is the category MISSING; every other value is a category, also numbers.
import { baseOrder } from '../enumeration/config-id.js';

export const MISSING = '(missing)';
const SEPARATORS = [',', ';'];

// Records of the text: [{ fields: [{ value, quoted }], line }] (line = 1-based line of the first field).
function records(text, separator) {
    const list = [];
    let fields = [], value = '', quoted = false, inQuotes = false, line = 1, start = 1, quoteLine = 0;
    const endField = () => { fields.push({ value, quoted }); value = ''; quoted = false; };
    const endRecord = () => {
        endField();
        if (fields.length > 1 || fields[0].quoted || fields[0].value.trim() !== '') list.push({ fields, line: start });
        fields = [];
    };
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') { value += '"'; i++; } else inQuotes = false;
            } else {
                if (c === '\n' || (c === '\r' && text[i + 1] !== '\n')) line++;
                value += c;
            }
        } else if (c === '"' && value.trim() === '') {
            inQuotes = true; quoted = true; value = ''; quoteLine = line;
        } else if (c === separator) {
            endField();
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n') i++;
            endRecord();
            line++; start = line;
        } else if (!quoted || c.trim() !== '') {
            // Characters after a closing quote belong to the field; blanks there are dropped.
            value += c;
        }
    }
    if (inQuotes) throw new Error(`Line ${quoteLine}: a quoted field is not closed.`);
    if (value !== '' || quoted || fields.length > 0) endRecord();
    return list;
}

function detectSeparator(text) {
    const counts = SEPARATORS.map(() => 0);
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === '"') inQuotes = !inQuotes;
        else if (!inQuotes && (c === '\n' || c === '\r') && text.slice(0, i).trim() !== '') break;
        else if (!inQuotes && SEPARATORS.includes(c)) counts[SEPARATORS.indexOf(c)]++;
    }
    return counts[1] > counts[0] ? SEPARATORS[1] : SEPARATORS[0];
}

/**
 * @returns { header, rows: [{ attribute: category }], report: { separator, rows, attributes: [{ name, categories,
 *            missing }], singleCategory: [names] } }; throws an Error with a message for the analyst
 */
export function parseUserCsv(text) {
    text = String(text).replace(/^﻿/, '');
    const separator = detectSeparator(text);
    const list = records(text, separator);
    if (list.length === 0) throw new Error('The file is empty.');
    const header = list[0].fields.map(f => f.value.trim());
    if (header.length < 2) throw new Error(`The file needs at least two attributes (separator "," or ";"); found ${header.length}.`);
    header.forEach((name, i) => {
        if (name === '') throw new Error(`Column ${i + 1} has no name in the header.`);
        if (name === '__proto__') throw new Error('The attribute name "__proto__" is not supported.');
        if (header.indexOf(name) !== i) throw new Error(`The attribute name "${name}" occurs twice in the header.`);
    });
    if (list.length === 1) throw new Error('The file has a header, but no rows.');
    const ragged = list.slice(1).filter(r => r.fields.length !== header.length);
    if (ragged.length > 0) {
        throw new Error(`Line ${ragged[0].line} has ${ragged[0].fields.length} fields, the header has ${header.length}` +
            (ragged.length > 1 ? ` (${ragged.length} such lines).` : '.'));
    }
    const missing = header.map(() => 0);
    const rows = list.slice(1).map(record => {
        const row = {};
        record.fields.forEach((f, j) => {
            const value = f.quoted ? f.value : f.value.trim();
            if (value === '') missing[j]++;
            row[header[j]] = value === '' ? MISSING : value;
        });
        return row;
    });
    const attributes = header.map((name, j) => ({ name, categories: new Set(rows.map(r => r[name])).size, missing: missing[j] }));
    return {
        header, rows,
        report: { separator, rows: rows.length, attributes, singleCategory: attributes.filter(a => a.categories === 1).map(a => a.name) },
    };
}

// One line on a loaded file for the analyst (status line of the tool).
export function describeReport(report, ribbons = null) {
    const plural = (n, word) => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;
    const missing = report.attributes.reduce((sum, a) => sum + a.missing, 0);
    let text = `${plural(report.rows, 'row')}, ${plural(report.attributes.length, 'attribute')} (`
        + report.attributes.map(a => `${a.name}: ${a.categories}`).join(', ') + ' categories)' + (ribbons === null ? '' : `, ${plural(ribbons, 'ribbon')}`);
    if (missing > 0) text += `; ${plural(missing, 'empty field')} shown as "${MISSING}"`;
    if (report.singleCategory.length > 0) text += `; one category only: ${report.singleCategory.join(', ')}`;
    return text + '. The file is processed in your browser and not sent to the server.';
}

// Dataset as createDataset of dataset-loader.js, marked as a dataset of the analyst.
export function createUserDataset(name, text) {
    const { header, rows, report } = parseUserCsv(text);
    return { name, header, rows, user: true, report };
}

// Configuration shown after loading: attributes in the order of the header, categories by first appearance.
export function initialConfig(dataset) {
    return baseOrder(dataset);
}
